/**
 * TaskPriorityBadge Component
 *
 * Displays a badge for task priority with consistent styling.
 * Follows the color scheme defined in the SPEC.
 *
 * @module components/tasks/TaskPriorityBadge
 */

import { memo } from 'react';
import {
  ArrowDown,
  Minus,
  ArrowUp,
  AlertTriangle
} from 'lucide-react';

// ============================================
// Constants
// ============================================

/**
 * Task priority configuration
 * Colors and icons for each priority level
 */
export const TASK_PRIORITY_CONFIG = {
  low: {
    bg: 'bg-gray-500/20',
    text: 'text-gray-400',
    border: 'border-gray-500/30',
    label: 'Low',
    Icon: ArrowDown
  },
  medium: {
    bg: 'bg-blue-500/20',
    text: 'text-blue-400',
    border: 'border-blue-500/30',
    label: 'Medium',
    Icon: Minus
  },
  high: {
    bg: 'bg-orange-500/20',
    text: 'text-orange-400',
    border: 'border-orange-500/30',
    label: 'High',
    Icon: ArrowUp
  },
  critical: {
    bg: 'bg-red-500/20',
    text: 'text-red-400',
    border: 'border-red-500/30',
    label: 'Critical',
    Icon: AlertTriangle
  }
};

/**
 * Size variants for the badge
 */
const SIZE_CLASSES = {
  sm: {
    badge: 'px-1.5 py-0.5 text-xs',
    icon: 'w-3 h-3'
  },
  md: {
    badge: 'px-2 py-1 text-xs',
    icon: 'w-3.5 h-3.5'
  },
  lg: {
    badge: 'px-3 py-1.5 text-sm',
    icon: 'w-4 h-4'
  }
};

// ============================================
// Component
// ============================================

/**
 * TaskPriorityBadge Component
 *
 * @param {object} props - Component props
 * @param {string} props.priority - Task priority value
 * @param {string} [props.size='md'] - Badge size: 'sm', 'md', or 'lg'
 * @param {boolean} [props.showIcon=true] - Whether to show the priority icon
 * @param {boolean} [props.showLabel=true] - Whether to show the priority label
 * @param {boolean} [props.iconOnly=false] - Show only icon (overrides showLabel)
 * @param {string} [props.className=''] - Additional CSS classes
 * @returns {React.ReactElement} Badge element
 */
const TaskPriorityBadge = memo(function TaskPriorityBadge({
  priority,
  size = 'md',
  showIcon = true,
  showLabel = true,
  iconOnly = false,
  className = ''
}) {
  const config = TASK_PRIORITY_CONFIG[priority];

  if (!config) {
    console.warn(`Unknown task priority: ${priority}`);
    return null;
  }

  const { bg, text, border, label, Icon } = config;
  const sizeConfig = SIZE_CLASSES[size] || SIZE_CLASSES.md;

  // Icon only mode - no label, smaller padding
  if (iconOnly) {
    return (
      <span
        className={`
          inline-flex items-center justify-center rounded-full
          border ${bg} ${text} ${border}
          p-1
          ${className}
        `}
        title={label}
      >
        <Icon className={sizeConfig.icon} />
      </span>
    );
  }

  return (
    <span
      className={`
        inline-flex items-center gap-1 rounded-full font-medium
        border ${bg} ${text} ${border}
        ${sizeConfig.badge}
        ${className}
      `}
    >
      {showIcon && <Icon className={sizeConfig.icon} />}
      {showLabel && <span>{label}</span>}
    </span>
  );
});

export default TaskPriorityBadge;
